import React from 'react'

import Loading from '../../utilities/loading'
import CaptureVideo from '../records/media/CaptureVideo'
import { fetchUnlabeledFailures } from '../records/media/diagnosticAssets'
import { formatDateTime } from '../../../lib/formatText'
import './snap-failures.css'

export const advanceAfterSkip = (items, currentPackageId) => {
  const remaining = items.filter((item) => item.packageId !== currentPackageId)
  return {
    items: remaining,
    current: remaining[0] || null,
  }
}

export const facilityLabel = ({ facilityName, facilityCode, scannerCode }) => {
  const parts = []
  if (facilityName) parts.push(facilityName)
  else if (facilityCode) parts.push(facilityCode)
  if (scannerCode) parts.push(`scanner ${scannerCode}`)
  return parts.join(' · ') || null
}

export const statusCodes = ({
  snapScanStatus,
  prismScanStatus,
  snapScanError,
}) => {
  const codes = []
  if (snapScanStatus) codes.push(snapScanStatus)
  if (prismScanStatus && prismScanStatus !== snapScanStatus) {
    codes.push(`Prism ${prismScanStatus}`)
  }
  return {
    codes,
    detail: snapScanError || null,
  }
}

export const unlabeledCountLabel = (total, scope) => {
  const count = Number.isFinite(total) ? total : 0
  const noun = scope === 'all' ? 'snaps' : 'errors'
  return `${count.toLocaleString()} unlabeled ${noun}`
}

export default function SnapFailures() {
  const [items, setItems] = React.useState([])
  const [current, setCurrent] = React.useState(null)
  const [total, setTotal] = React.useState(0)
  const [scope, setScope] = React.useState('errors')
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const loadRequest = React.useRef(0)

  const loadItems = React.useCallback(() => {
    const request = ++loadRequest.current
    setLoading(true)
    setError(null)
    return fetchUnlabeledFailures({ limit: 20, scope })
      .then((payload) => {
        if (loadRequest.current !== request) return
        const nextItems = payload?.items || []
        setItems(nextItems)
        setCurrent(nextItems[0] || null)
        setTotal(
          typeof payload?.total === 'number' ? payload.total : nextItems.length
        )
      })
      .catch(() => {
        if (loadRequest.current !== request) return
        setError('Could not load unlabeled Snap failures.')
        setItems([])
        setCurrent(null)
        setTotal(0)
      })
      .finally(() => {
        if (loadRequest.current === request) setLoading(false)
      })
  }, [scope])

  React.useEffect(() => {
    loadItems()
    return () => {
      loadRequest.current += 1
    }
  }, [loadItems])

  const handleLabeled = () => {
    loadItems()
  }

  const handleSkip = () => {
    if (!current) return
    const next = advanceAfterSkip(items, current.packageId)
    setItems(next.items)
    setCurrent(next.current)
    if (!next.current) {
      loadItems()
    }
  }

  const place = current
    ? facilityLabel({
        facilityName: current.facilityName,
        facilityCode: current.facilityCode,
        scannerCode: current.scannerCode,
      })
    : null
  const codes = current
    ? statusCodes({
        snapScanStatus: current.snapScanStatus,
        prismScanStatus: current.prismScanStatus,
        snapScanError: current.snapScanError,
      })
    : { codes: [], detail: null }
  const takenAt = current?.capturedAt || current?.recordDate

  return (
    <div className="mx-4 snap-failures">
      <header className="snap-failures-top">
        <div>
          <h1 className="snap-failures-title">Snap Failures</h1>
          <p className="snap-failures-job">
            Watch the clip. Choose why it failed.
          </p>
        </div>
        <div className="snap-failures-tools">
          <div
            className="snap-failures-filters"
            role="group"
            aria-label="Scan filter"
          >
            <button
              type="button"
              className={`snap-failures-filter${
                scope === 'errors' ? ' is-selected' : ''
              }`}
              aria-pressed={scope === 'errors'}
              onClick={() => setScope('errors')}
            >
              Errors
            </button>
            <button
              type="button"
              className={`snap-failures-filter${
                scope === 'all' ? ' is-selected' : ''
              }`}
              aria-pressed={scope === 'all'}
              onClick={() => setScope('all')}
            >
              All snaps
            </button>
          </div>
          {!loading && !error ? (
            <div className="snap-failures-count" aria-live="polite">
              {unlabeledCountLabel(total, scope)}
            </div>
          ) : null}
        </div>
      </header>

      {loading ? (
        <Loading />
      ) : error ? (
        <div className="snap-failures-error" role="alert">
          {error}
          <div className="mt-3">
            <button type="button" className="btn btn-dark" onClick={loadItems}>
              Try again
            </button>
          </div>
        </div>
      ) : !current ? (
        <div className="snap-failures-empty">
          {scope === 'all'
            ? 'You’re caught up — no unlabeled Snap scans right now.'
            : 'You’re caught up — no unlabeled Snap errors right now.'}
        </div>
      ) : (
        <>
          <div className="snap-failures-context">
            <div className="snap-failures-context-primary">
              {takenAt ? (
                <div>
                  <span className="snap-failures-kicker">Taken</span>{' '}
                  {formatDateTime(takenAt)}
                </div>
              ) : null}
              {place ? (
                <div>
                  <span className="snap-failures-kicker">Facility</span> {place}
                </div>
              ) : null}
              <div>
                <span className="snap-failures-kicker">Package</span>{' '}
                <code>{current.packageId}</code>
              </div>
              <div>
                <span className="snap-failures-kicker">Prism</span>{' '}
                {current.prismScanId ? <code>{current.prismScanId}</code> : '—'}
              </div>
            </div>
            <div className="snap-failures-context-secondary">
              {codes.codes.length > 0 ? (
                <div>
                  <span className="snap-failures-kicker">Status</span>{' '}
                  <code>{codes.codes.join(' · ')}</code>
                </div>
              ) : null}
              {codes.detail ? (
                <div
                  className="snap-failures-error-detail"
                  title={codes.detail}
                >
                  <span className="snap-failures-kicker">Error</span>{' '}
                  {codes.detail}
                </div>
              ) : null}
            </div>
          </div>
          <CaptureVideo
            key={current.packageId}
            variant="session"
            scanPackageId={current.packageId}
            onFailureTypeSaved={handleLabeled}
            secondaryAction={
              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={handleSkip}
              >
                Skip
              </button>
            }
          />
        </>
      )}
    </div>
  )
}
